// FOUNDER CORRECTION 2026-09-25 (staging browser walk, item 1) — "THE /cart PAGE SHOULD NOT EXIST."
//
// The cart DRAWER stays. What went is the standalone `/cart` screen, which was a third place to review
// a basket (after the drawer and the funnel's own MY CART step) and the only one not built from the
// Kit. Every entry point that used to open `/cart` now opens the approved in-funnel review page for the
// basket's OWN mountain — `/mountain-<code>/booking/review` — where MY CART and CARD DETAILS live.
//
// NOTHING IS MOVED OR COPIED TO GET THERE. The review page reads the same two stores the old page read
// (the host `sg-cart` store and the design's `sg-cart-v1`), so following this link keeps the cart.
//
// Which mountain: the basket's own (a host line carries `mountain`), else the mountain the visitor last
// stood on (`gf.mountain`, the same rule the old page's chrome used). That fallback is a NAVIGATION
// default, not a claim about the basket: an empty basket on the review page renders the Kit's own
// "Your cart is empty" state rather than anything invented.

import { designCartCheckoutHref } from "@/components/marketplace/DesignCartLines";

const CODE = /^[a-z0-9-]+$/;

/** The one review URL every cart entry point opens. */
export function cartReviewHref(
  hostItems: readonly { mountain?: string | null }[],
  rememberedMountain: string | null | undefined,
  fallbackMountain = "whistler",
): string {
  const candidates = [...hostItems.map((i) => i.mountain), rememberedMountain, fallbackMountain];
  const code = candidates
    .map((c) => String(c ?? "").replace(/^mountain-/, "").trim().toLowerCase())
    .find((c) => c !== "" && CODE.test(c));
  return designCartCheckoutHref(code ?? fallbackMountain);
}

/**
 * FOUNDER RULING 2026-09-26 — an old `/cart?…` or `/checkout/complete?…` deep link keeps its ORDER
 * CONTEXT on the way to the review page: `order`/`sg_order` becomes `sg_order`, and Stripe's own return
 * parameters pass through unchanged so the review page can read the server's status for that order.
 * Nothing else in the query is forwarded — this is an allow-list, not a copy of `location.search`.
 */
export function withReturnContext(href: string, search: string): string {
  const from = new URLSearchParams(search);
  const to = new URLSearchParams();
  // An EMPTY `sg_order=` falls through to `order` — deliberately not `??`, which would keep the empty string.
  const primary = from.get("sg_order");
  const order = primary !== null && primary !== "" ? primary : from.get("order");
  if (order) to.set("sg_order", order);
  for (const k of ["redirect_status", "payment_intent", "payment_intent_client_secret"]) {
    const v = from.get(k);
    if (v) to.set(k, v);
  }
  const q = to.toString();
  return q ? `${href}?${q}` : href;
}
